/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { callApi } from 'api/helpers/api.helpers';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DashboardIcon from '@material-ui/icons/Dashboard';
import draftApi from '../../api/draft.api';
import useAttachments from '../../components/preview/useAttachments';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import useProcedure from '../../procedure/useProcedure';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import SignIcon from '../../components/icons/SignIcon';

export default function useSendConfirmation() {
  const [submission, setSubmission] = useState(null);
  const [previewObject, setPreviewObject] = useState(null);
  const { attachments, loadAttachmentFile } = useAttachments(submission);

  const { t } = useTranslation();

  const location = useLocation();
  const match = useRouteMatch();
  const submissionId = match.params?.submissionId;
  const currentProcedure = useProcedure();
  const submissionUiSchema = useConfiguration(ConfigResourceProps.SUBMISSION_UI_SCHEMA, currentProcedure.id);

  useEffect(() => {
    (async () => {
      if (submissionId) {
        const draftTmp = await callApi(draftApi.getById)(submissionId);
        setSubmission(draftTmp);
        setPreviewObject({ id: 'receipt', path: null });
      }
    })();
  }, [setSubmission, submissionId]);

  const loadReceiptFile = useCallback(() => {
    (async () => {
      if (submission && submission.receipt) {
        const [filePath] = await callApi(draftApi.openFile)(
          submission.id,
          submission.receipt.receiptFile.id,
          'Receipt',
          'application/pdf',
          '/receipt',
        );

        setPreviewObject({ id: 'receipt', path: filePath });
      }
    })();
  }, [submission]);

  useEffect(() => {
    loadReceiptFile();
  }, [submission]);

  const loadAttachmentFileMemo = useCallback(
    (file) => {
      (async () => {
        const attachmentFile = await loadAttachmentFile(file, submission);
        setPreviewObject(attachmentFile);
      })();
    },
    [submission?.id],
  );

  const handleReceiptClick = async () => {
    loadReceiptFile();
    setPreviewObject({ id: 'receipt', path: null });
  };

  const handleSignaturesClick = async () => {
    setPreviewObject({ id: 'signature', path: null });
  };

  const handleSummaryClick = () => {
    setPreviewObject({ id: 'summary', path: null });
  };

  const handleSubmissionClick = useCallback(() => {
    (async () => {
      const filePath = await callApi(draftApi.openSubmission)(submission.id);
      setPreviewObject({ id: 'submission', path: filePath });
    })();
  }, [submission?.id]);

  const topSidebarEntries = [
    {
      id: 'submission',
      title: t('submission.file'),
      subtitle: ' ',
      handleEntryClick: handleSubmissionClick,
    },
  ];

  const topSelectEntries = [
    {
      id: 'receipt',
      title: t('Receipt'),
      subtitle: ' ',
      icon: <PictureAsPdfIcon fontSize="small" />,
      handleEntryClick: handleReceiptClick,
    },
    {
      id: 'signature',
      title: t('submission.signatures'),
      subtitle: ' ',
      icon: <SignIcon />,
      handleEntryClick: handleSignaturesClick,
    },
    {
      id: 'summary',
      title: t('summary'),
      subtitle: ' ',
      icon: <DashboardIcon fontSize="small" />,
      handleEntryClick: handleSummaryClick,
    },
  ].concat(topSidebarEntries);

  return {
    submission,
    uiSchema: submissionUiSchema.resource,
    topSelectEntries,
    topSidebarEntries,
    attachments,
    previewObject,
    location,
    t,
    handleReceiptClick,
    handleSignaturesClick,
    handleSummaryClick,
    loadAttachmentFile: loadAttachmentFileMemo,
  };
}
